import {
  type DetachedWindowPrewarmOptions,
  openDetachedWindow,
  prewarmDetachedWindow,
} from "./detachedWindows";
import { isTauriRuntime } from "./environment";

/**
 * A second (third, fourth…) window showing the whole application, as opposed to the single-purpose
 * detached surfaces in `detachedWindows`. Cmd+clicking a link opens one of these, which is the
 * desktop equivalent of the new tab a browser would have given the user.
 *
 * A window is nothing but the application at a URL — that is the whole of what gets handed to it,
 * and the only thing a new kind of window has to say for itself is its label.
 */

export const APP_WINDOW_SURFACE = "app-window";

export interface AppWindowPayload {
  path: string;
}

/**
 * Matches the `main` window in tauri.conf.json. A cmd+clicked window is the same application, so it
 * gets the same geometry and the same hidden-title-bar treatment — the app draws its own header and
 * reserves space for the traffic lights, which lands wrong under any other title bar style.
 */
const APP_WINDOW_OPTIONS: DetachedWindowPrewarmOptions = {
  surface: APP_WINDOW_SURFACE,
  // The window is the application, not one of its surfaces — see `currentWindowKind`.
  kind: "application",
  /** Until the window's route resolves and it renames itself from `document.title`. */
  title: "Fraction LOS",
  width: 1440,
  height: 900,
  minWidth: 1100,
  minHeight: 720,
  // Cmd+clicking twice gives two windows, so they step down from each other rather than stack.
  placement: "cascade",
};

/**
 * Boot a hidden application window so the next cmd+click is a reveal rather than a cold start.
 *
 * A new webview pays for the bundle, the React root, LaunchDarkly, auth and the query cache before
 * it can show anything; warming one moves all of that off the click. Opening consumes the warm slot
 * and starts the replacement, so the cost lands between clicks instead of on them.
 */
export function prewarmAppWindow(): Promise<void> {
  return prewarmDetachedWindow(APP_WINDOW_OPTIONS);
}

export type PrimaryModifier = "meta" | "ctrl";

interface NewWindowClick {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
  target: EventTarget | null;
}

/** Cmd on macOS, Ctrl everywhere else — ctrl+click on a Mac is a right click, not a new window. */
export function platformPrimaryModifier(): PrimaryModifier {
  const platform = typeof navigator === "undefined" ? "" : navigator.platform || navigator.userAgent || "";
  return /Mac|iPhone|iPad/.test(platform) ? "meta" : "ctrl";
}

/**
 * The in-app path a click asks to open in its own window, or null to leave the click alone.
 *
 * Only same-origin links qualify: an external link already goes to the system browser, and a
 * `mailto:` or `tel:` belongs to whatever handles it. Pure hash links stay in place — `href="#"` is
 * how half the app's buttons are spelled, and an in-page anchor is not a destination.
 */
export function resolveNewWindowLink(
  event: NewWindowClick,
  { currentUrl, primaryModifier }: { currentUrl: string; primaryModifier: PrimaryModifier }
): string | null {
  if (event.defaultPrevented) return null;

  const middleClick = event.button === 1;
  if (!middleClick) {
    if (event.button !== 0 || event.altKey) return null;
    const held =
      primaryModifier === "meta" ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
    if (!held) return null;
  }

  const target = event.target;
  if (!(target instanceof Element)) return null;
  const anchor = target.closest("a[href]");
  if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute("download")) return null;

  const href = anchor.getAttribute("href")?.trim();
  if (!href || href.startsWith("#")) return null;

  let destination: URL;
  let current: URL;
  try {
    current = new URL(currentUrl);
    destination = new URL(href, currentUrl);
  } catch {
    return null;
  }
  if (destination.origin !== current.origin) return null;

  return `${destination.pathname}${destination.search}${destination.hash}`;
}

/**
 * Open the application at `path` in its own native window.
 *
 * Goes through the same surface machinery as every other window: a prewarmed shell is claimed by
 * writing its payload, and the shell — already booted — navigates to `path` client-side.
 */
export async function openAppWindow(
  path: string,
  { focus = true }: { focus?: boolean } = {}
): Promise<string> {
  if (!isTauriRuntime()) throw new Error("Extra windows are only available in the desktop app.");

  return openDetachedWindow<AppWindowPayload>({
    ...APP_WINDOW_OPTIONS,
    // Every cmd+click gets its own window, exactly as every cmd+click gets its own browser tab, so
    // the instance is fresh rather than derived from the path.
    instanceKey: crypto.randomUUID(),
    payload: { path },
    focus,
  });
}
